import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '../../../apiClient';

export type HelpArticleDto = {
  id: string;
  title: string;
  slug: string;
  contentKey: string;
  excerpt?: string;
  content: string;
  language: string;
  status: string;
  categoryId: string;
  coverImage?: string;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
};

export const GET_HELP_ARTICLES_QUERY_KEY = ['help-articles'];
export const GET_HELP_ARTICLE_QUERY_KEY = (id: string) => ['help-article', id];
export const GET_PUBLIC_HELP_ARTICLES_QUERY_KEY = (lang: string) => ['help-public-articles', lang];

export const useHelpArticles = (filters?: { lang?: string; categoryId?: string; status?: string }) => {
  const client = useApiClient();
  const params = new URLSearchParams();
  if (filters?.lang) params.set('lang', filters.lang);
  if (filters?.categoryId) params.set('categoryId', filters.categoryId);
  if (filters?.status) params.set('status', filters.status);
  const qs = params.toString();

  return useQuery({
    queryKey: [...GET_HELP_ARTICLES_QUERY_KEY, filters],
    queryFn: () => client.fetchApi<HelpArticleDto[], {}>({
      endpoint: `help/admin/articles${qs ? `?${qs}` : ''}`,
      method: 'GET',
    }),
    staleTime: 5 * 60 * 1000,
  });
};

export const useHelpArticle = (id: string) => {
  const client = useApiClient();
  return useQuery({
    queryKey: GET_HELP_ARTICLE_QUERY_KEY(id),
    queryFn: () => client.fetchApi<HelpArticleDto, {}>({ endpoint: `help/admin/articles/${id}`, method: 'GET' }),
    enabled: !!id,
  });
};

export const usePublicHelpArticles = (lang: string) => {
  const client = useApiClient();
  return useQuery({
    queryKey: GET_PUBLIC_HELP_ARTICLES_QUERY_KEY(lang),
    queryFn: () => client.fetchApi<HelpArticleDto[], {}>({ endpoint: `help/articles?lang=${lang}`, method: 'GET' }),
    staleTime: 5 * 60 * 1000,
  });
};

export const usePublicHelpArticle = (slug: string, lang: string) => {
  const client = useApiClient();
  return useQuery({
    queryKey: ['help-public-article', slug, lang],
    queryFn: () => client.fetchApi<HelpArticleDto, {}>({ endpoint: `help/articles/${slug}?lang=${lang}`, method: 'GET' }),
    enabled: !!slug,
    staleTime: 5 * 60 * 1000,
  });
};
